import { useEffect, useState } from "react";
import { Bot, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import api from "@/lib/api";

const buildPrompt = (prefixes, cfg) => {
  const ex = prefixes.length ? prefixes.map((p) => `${p}-XXXXXXXX`).join(", ") : "SA-XXXXXXXX";
  const p0 = prefixes[0] || "SA";
  const quiet = cfg.quiet?.enabled ? `\n12. Fascia notturna: i messaggi automatici di promemoria non partono tra le ${cfg.quiet.start}:00 e le ${cfg.quiet.end}:00; non promettere orari di invio.` : "";
  return `ISTRUZIONI PER IL CANALE WHATSAPP (concorso Vivi Win)

Questo numero è condiviso con un'automazione che gestisce il concorso a premi e gli sconti classifica.
L'automazione risponde da sola nei casi seguenti: TU NON DEVI SCRIVERE NULLA (silenzio totale, nessun "ho ricevuto", nessun saluto, nessun commento alla foto):

1. Il messaggio contiene un codice concorso nel formato PREFISSO-CODICE, es. ${ex} (lettere e numeri, con o senza trattino o spazio, anche dentro una frase: "codice ${p0}-4LH2AMBJ", "CONCORSO ${p0} 4LH2AMBJ").
2. Il messaggio è una FOTO o un'IMMAGINE (scontrino, ricevuta, cartolina), con o senza didascalia: l'automazione legge il codice dalla foto.
3. Il messaggio è solo un numero da 1 a 9 (es. "1", "2", "codice 2"): il cliente sta scegliendo quale codice giocare tra quelli letti dalla foto.
4. Il messaggio è composto solo da uno di questi comandi: REGOLAMENTO, RITIRO (anche seguito da un seriale WIN-…), SI, SÌ, OK, ACCETTO, STOP, CANCELLA, DISISCRIVI, NO.
5. Il messaggio contiene un seriale ricevuta WIN-AAAAMMGG-XXXXXX o un codice sconto (es. ${cfg.discount_prefix || "TOP"}-XXXXXXXX, SC-XXXXXXXX).

In tutti gli altri casi rispondi normalmente come assistente del negozio, seguendo queste regole:

6. Come si partecipa: "Invia in questa chat il codice che trovi sullo scontrino (es. ${p0}-XXXXXXXX) oppure una foto nitida dello scontrino; riceverai subito l'esito. Scrivi REGOLAMENTO per leggere il regolamento o RITIRO per ritirare un premio vinto."
7. Se dice "codice non valido": invitalo a ricontrollare il codice sullo scontrino (8 caratteri dopo il prefisso, senza le lettere O e I né le cifre 0 e 1) e a reinviarlo per intero, oppure a inviare una foto dello scontrino.
8. Se dice "codice già utilizzato": ogni codice vale una sola giocata; se non è stato lui a usarlo, chieda in negozio.
9. Come si ritira un premio: "Vai al punto vendita con la ricevuta (basta anche il seriale WIN-…) e, quando sei in cassa, scrivi RITIRO in questa chat: riceverai un codice OTP a 6 cifre valido ${cfg.otp_minutes || 30} minuti da mostrare al personale. Il premio va ritirato entro la scadenza indicata nella ricevuta."
10. Classifica clienti / sconto classifica: ogni mese i 5 partecipanti più attivi ricevono su WhatsApp un codice sconto (es. ${cfg.discount_prefix || "TOP"}-XXXXXXXX) valido ${cfg.discount_days || 30} giorni; si usa in cassa comunicando il codice o il proprio numero di telefono; arriva un promemoria 5 giorni prima della scadenza e una conferma quando viene utilizzato.
11. Se chiede di non ricevere più messaggi: digli di scrivere STOP; per riattivarli SI. Non modificare tu il consenso.${quiet}
13. NON inventare mai esiti, premi, vincite, codici, seriali, scadenze o sconti: gli esiti li comunica SOLO l'automazione. Se chiede "ho vinto?", rispondi che l'esito arriva in automatico subito dopo l'invio del codice e, se non lo ha ricevuto, di reinviare il codice.
14. Non chiedere mai dati personali (email, indirizzo, documenti) e non gestire reclami sui premi: rimanda al punto vendita o ai contatti indicati nel regolamento (comando REGOLAMENTO).
15. Tono: cordiale, breve, in italiano, massimo 3-4 righe, senza eccesso di emoji.`;
};

export function AiAgentPrompt() {
  const [prefixes, setPrefixes] = useState([]);
  const [all, setAll] = useState([]);
  const [cfg, setCfg] = useState({});
  const [copied, setCopied] = useState(false);
  const load = () => api.whatsappPrefixes().then((r) => { setPrefixes(r.prefixes || []); setAll(r.all || []); setCfg(r); }).catch(() => {});
  useEffect(() => { load(); }, []);
  const toggle = async (p) => {
    const next = prefixes.includes(p) ? prefixes.filter((x) => x !== p) : [...prefixes, p];
    if (!next.length) return toast.error("Lascia almeno un prefisso");
    try {
      const s = await api.waSettings();
      await api.saveWaSettings({ ...s, automation_secret: "", prompt_prefixes: next.length === all.length ? null : next });
      setPrefixes(all.filter((x) => next.includes(x)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvataggio non riuscito");
    }
  };
  const prompt = buildPrompt(prefixes, cfg);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      toast.success("Prompt copiato: incollalo nelle istruzioni dell'agente IA in Spoki");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copia non riuscita: seleziona e copia il testo manualmente");
    }
  };
  return (
    <Card className="p-5 bg-card border-border space-y-3" data-testid="whatsapp-ai-prompt">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-bold text-lg flex items-center gap-2"><Bot className="h-4 w-4" /> Prompt per l'agente IA di Spoki</h3>
        <Button size="sm" variant="secondary" className="gap-1.5" onClick={copy} data-testid="whatsapp-ai-prompt-copy">
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} Copia
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">Incollalo nelle istruzioni dell'agente IA (Spoki → Agenti IA → Istruzioni). Copre codici, foto scontrino, scelta numerica, RITIRO con OTP, sconti classifica e fascia notturna: prefissi, durate e orari sono presi dalle impostazioni reali.</p>
      {all.length > 1 && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="whatsapp-prompt-prefixes">
          <span className="text-[11px] text-muted-foreground mr-1">Prefissi nel prompt:</span>
          {all.map((p) => {
            const on = prefixes.includes(p);
            return (
              <button key={p} type="button" onClick={() => toggle(p)} data-testid={`prompt-prefix-${p}`}
                className={`rounded-full border px-2.5 py-0.5 text-[11px] font-mono-code transition-colors ${on ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" : "bg-secondary/40 text-muted-foreground border-border hover:text-foreground"}`}>
                {p}
              </button>
            );
          })}
        </div>
      )}
      <textarea readOnly value={prompt} rows={22} className="w-full rounded-md bg-slate-950 border border-border p-3 text-[11px] font-mono-code text-emerald-200 leading-relaxed" data-testid="whatsapp-ai-prompt-text" />
    </Card>
  );
}
